import { useEffect } from "react";

/**
 * App-wide registry of mounted forms that hold input a page reload would
 * lose. The update flow reads it before any reload it starts on its own
 * (next-navigation reload, stale-chunk reload); while any form is dirty the
 * registry also arms the browser's native "leave site?" prompt, so a manual
 * refresh (including the update banner's button) cannot silently drop input.
 */
const dirtySources = new Set<symbol>();
let beforeUnloadArmed = false;

function onBeforeUnload(event: BeforeUnloadEvent): void {
  event.preventDefault();
  // Legacy browsers only show the prompt when returnValue is set.
  event.returnValue = "";
}

function syncBeforeUnload(): void {
  const shouldArm = dirtySources.size > 0;
  if (shouldArm === beforeUnloadArmed) return;
  beforeUnloadArmed = shouldArm;
  if (shouldArm) {
    globalThis.addEventListener("beforeunload", onBeforeUnload);
  } else {
    globalThis.removeEventListener("beforeunload", onBeforeUnload);
  }
}

/** True while at least one mounted form reports unsaved input. */
export function hasUnsavedChanges(): boolean {
  return dirtySources.size > 0;
}

/**
 * Report whether this component holds unsaved input. The registration is
 * dropped automatically on unmount.
 */
export function useUnsavedChanges(dirty: boolean): void {
  useEffect(() => {
    if (!dirty) return;
    const token = Symbol("unsaved-changes");
    dirtySources.add(token);
    syncBeforeUnload();
    return () => {
      dirtySources.delete(token);
      syncBeforeUnload();
    };
  }, [dirty]);
}
